import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3104";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export interface U3104InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode?: string;
}

export interface U3104AccountingYearSearchRow {
  id: string;
  yearCode?: string;
  version: number;
  deletedAt?: string | null;
  createdAt?: string;
}

export interface U3104AccountingPeriodApi {
  id?: string;
  periodNumber: number;
  startAt: string;
  endAt: string;
  version?: number;
}

/** GET /U3104/:id — sans organization */
export interface U3104AccountingYearGet {
  id: string;
  yearCode: string;
  version: number;
  accountingPeriods: U3104AccountingPeriodApi[];
}

export interface U3104AccountingYearOrganization {
  id: string;
  uuid?: string;
  organizationName?: string;
}

export interface U3104AccountingYearWithOrg extends U3104AccountingYearGet {
  organization?: U3104AccountingYearOrganization;
}

export interface U3104AccountingPeriodFormRow {
  id?: string;
  periodNumber: number;
  startAt: string;
  endAt: string;
  version?: number;
}

export type U3104AccountingYearFormRow = {
  id: string;
  yearCode: string;
  version: number;
  accountingPeriods: U3104AccountingPeriodFormRow[];
};

/**
 * 12 périodes générées comme pour un premier jour **1er janvier** de `year` (UTC).
 * Champs formulaire en yyyy-MM-dd ; envoi API via toIsoBoundary / toIsoEndOfDayFromYmd.
 */
export function buildDefaultAccountingPeriodsForYear(year: number): U3104AccountingPeriodFormRow[] {
  return buildAccountingPeriodsFromFirstPeriodDay(`${year}-01-01`);
}

function utcMidnight(y: number, month0: number, day: number): Date {
  return new Date(Date.UTC(y, month0, day, 0, 0, 0, 0));
}

function utcEndOfDay(y: number, month0: number, day: number): Date {
  return new Date(Date.UTC(y, month0, day, 23, 59, 59, 0));
}

/** Valeur affichée dans les calendriers : jour civil UTC (évite 2030-01-01Z → veille en fuseau local). */
function formPeriodDateUtcYmd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function apiInstantToFormYmd(s: string): string {
  const t = String(s ?? "").trim();
  if (!t) return "";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return t.slice(0, 10);
  return formPeriodDateUtcYmd(d);
}

type DayKey = { y: number; m: number; d: number };

function calendarDayKeyFromDate(d: Date): DayKey {
  return { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate() };
}

/**
 * Début (minuit UTC) de la **période abstraite** n+1 : même quantième que le 1er jour de période 1, + `n` mois calendaires UTC
 * (même règle de débordement que `Date.UTC`, ex. 31 jan + 1 mois → mars).
 */
function abstractNextPeriodStartMidnightUtc(firstKey: DayKey, n: number): Date {
  return new Date(Date.UTC(firstKey.y, firstKey.m + n, firstKey.d, 0, 0, 0, 0));
}

/** Dernier dimanche (fin 23:59:59 UTC) strictement avant `nextStartMidnightUtc`. */
function lastSundayEndStrictlyBefore(nextStartMidnightUtc: Date): Date {
  const cur = new Date(nextStartMidnightUtc.getTime() - 24 * 60 * 60 * 1000);
  let y = cur.getUTCFullYear();
  let m0 = cur.getUTCMonth();
  let d = cur.getUTCDate();
  while (new Date(Date.UTC(y, m0, d)).getUTCDay() !== 0) {
    const prev = new Date(Date.UTC(y, m0, d - 1, 12, 0, 0, 0));
    y = prev.getUTCFullYear();
    m0 = prev.getUTCMonth();
    d = prev.getUTCDate();
  }
  return utcEndOfDay(y, m0, d);
}

/** Fin période `n` (1…11) : dernier dimanche avant le début abstrait de la période `n+1`. */
function periodEndLastSundayBeforeAbstractNext(firstKey: DayKey, n: number): Date {
  return lastSundayEndStrictlyBefore(abstractNextPeriodStartMidnightUtc(firstKey, n));
}

/** Fin période `n` (1…12) : veille (fin de journée UTC) du début abstrait de la période `n+1` (= premier jour + `n` mois). */
function periodEndDayBeforeAbstractNext(firstKey: DayKey, n: number): Date {
  const nextStart = abstractNextPeriodStartMidnightUtc(firstKey, n);
  const prev = new Date(nextStart.getTime() - 24 * 60 * 60 * 1000);
  return utcEndOfDay(prev.getUTCFullYear(), prev.getUTCMonth(), prev.getUTCDate());
}

/**
 * Jour civil UTC du 1er jour de période 1 : s’appuie sur yyyy-MM-dd (10 premiers caractères si chaîne ISO),
 * pour que l’heure (ex. T20:00:00Z) ne décale pas le mois/jour utilisé dans la génération des 12 périodes.
 */
function dayKeyFromFirstPeriodInput(input: Date | string): DayKey {
  const raw = typeof input === "string" ? input.trim() : input.toISOString();
  const head = raw.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    const [ys, ms, ds] = head.split("-");
    const y = Number(ys);
    const mo = Number(ms);
    const dayNum = Number(ds);
    const probe = new Date(Date.UTC(y, mo - 1, dayNum, 0, 0, 0, 0));
    if (probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === dayNum) {
      return { y, m: mo - 1, d: dayNum };
    }
  }
  const fallback = typeof input === "string" ? new Date(input) : input;
  return calendarDayKeyFromDate(fallback);
}

/** Lendemain du jour calendaire de `endOfDay` (fin 23:59:59 UTC). */
function nextCalendarDayStartUtc(endOfDay: Date): Date {
  const y = endOfDay.getUTCFullYear();
  const m0 = endOfDay.getUTCMonth();
  const d = endOfDay.getUTCDate();
  return utcMidnight(y, m0, d + 1);
}

/** Motif de date de fin des périodes (assistant création). */
export type U3104PeriodEndPattern = "secondSunday" | "startPlusMonthMinusOneDay";

export const U3104_PERIOD_END_PATTERN_DEFAULT: U3104PeriodEndPattern = "secondSunday";

export interface U3104BuildAccountingPeriodsOptions {
  /** Défaut : `secondSunday` (dernier dimanche avant le début abstrait pour p.1–11 ; p.12 = veille de premier jour + 12 mois). */
  periodEndPattern?: U3104PeriodEndPattern;
}

/**
 * 12 périodes, enchaînement : lendemain de la fin de chaque période.
 *
 * **Période abstraite** : pour l’indice de période `n`, le début « cible » de la période suivante est le **même quantième** que le 1er jour de période 1, **+ n mois** calendaires UTC (règle `Date.UTC`).
 *
 * - **`secondSunday`** (défaut) : p. **1 à 11** = **dernier dimanche** strictement avant ce début abstrait ; p. **12** = **veille** du début abstrait à **+12 mois** (= premier jour + 12 mois − 1 jour).
 * - **`startPlusMonthMinusOneDay`** : p. **1 à 12** = **veille** du début abstrait (même formule que la p.12 de l’autre mode).
 *
 * `firstPeriodDay` : chaîne `yyyy-MM-dd` ou ISO (10 premiers caractères), ou `Date`.
 */
export function buildAccountingPeriodsFromFirstPeriodDay(
  firstPeriodDay: Date | string,
  options?: U3104BuildAccountingPeriodsOptions,
): U3104AccountingPeriodFormRow[] {
  const pattern = options?.periodEndPattern ?? U3104_PERIOD_END_PATTERN_DEFAULT;
  const k = dayKeyFromFirstPeriodInput(firstPeriodDay);
  let start = utcMidnight(k.y, k.m, k.d);
  const periods: U3104AccountingPeriodFormRow[] = [];

  for (let n = 1; n <= 12; n += 1) {
    const end =
      n <= 11
        ? pattern === "startPlusMonthMinusOneDay"
          ? periodEndDayBeforeAbstractNext(k, n)
          : periodEndLastSundayBeforeAbstractNext(k, n)
        : periodEndDayBeforeAbstractNext(k, 12);
    periods.push({
      periodNumber: n,
      startAt: formPeriodDateUtcYmd(start),
      endAt: formPeriodDateUtcYmd(end),
    });
    if (n < 12) {
      start = nextCalendarDayStartUtc(end);
    }
  }

  return periods;
}

export function sortPeriodsByNumber(periods: U3104AccountingPeriodApi[]): U3104AccountingPeriodApi[] {
  return [...periods].sort((a, b) => (a.periodNumber ?? 0) - (b.periodNumber ?? 0));
}

export function normalizeU3104FromApi(row: U3104AccountingYearGet | U3104AccountingYearWithOrg): U3104AccountingYearFormRow {
  const sorted = sortPeriodsByNumber(row.accountingPeriods ?? []);
  return {
    id: row.id,
    yearCode: row.yearCode ?? "",
    version: row.version ?? 0,
    accountingPeriods: sorted.map((p) => ({
      id: p.id,
      periodNumber: p.periodNumber,
      startAt: apiInstantToFormYmd(p.startAt ?? ""),
      endAt: apiInstantToFormYmd(p.endAt ?? ""),
      version: p.version,
    })),
  };
}

export function formToSearchRow(f: U3104AccountingYearFormRow): U3104AccountingYearSearchRow {
  return {
    id: f.id,
    yearCode: f.yearCode,
    version: f.version,
  };
}

export function createEmptyU3104FormRow(): U3104AccountingYearFormRow {
  const y = new Date().getUTCFullYear();
  return {
    id: "",
    yearCode: "",
    version: 0,
    accountingPeriods: buildDefaultAccountingPeriodsForYear(y),
  };
}

export interface U3104AccountingPeriodCreateBody {
  periodNumber: number;
  startAt: string;
  endAt: string;
}

export interface U3104AccountingPeriodUpdateBody extends U3104AccountingPeriodCreateBody {
  id?: string;
  version?: number;
}

export interface U3104AccountingYearCreateBody {
  yearCode: string;
  accountingPeriods: U3104AccountingPeriodCreateBody[];
}

export interface U3104AccountingYearUpdateBody {
  id: string;
  yearCode: string;
  version: number;
  accountingPeriods: U3104AccountingPeriodUpdateBody[];
}

export interface U3104AccountingYearDeleteBody {
  id: string;
  version: number;
}

function toIsoBoundary(value: string): string {
  const s = String(value ?? "").trim();
  if (!s) return s;
  if (s.includes("T")) return new Date(s).toISOString();
  const ymd = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return new Date(`${ymd}T00:00:00.000Z`).toISOString();
  }
  return new Date(s).toISOString();
}

function toIsoEndOfDayFromYmd(value: string): string {
  const s = String(value ?? "").trim();
  if (!s) return s;
  if (s.includes("T")) return new Date(s).toISOString();
  const ymd = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return new Date(`${ymd}T23:59:59.000Z`).toISOString();
  }
  return new Date(s).toISOString();
}

export function buildCreateBody(data: U3104AccountingYearFormRow): U3104AccountingYearCreateBody {
  return {
    yearCode: data.yearCode.trim(),
    accountingPeriods: data.accountingPeriods.map((p) => ({
      periodNumber: p.periodNumber,
      startAt: toIsoBoundary(p.startAt),
      endAt: toIsoEndOfDayFromYmd(p.endAt),
    })),
  };
}

export function buildUpdateBody(data: U3104AccountingYearFormRow): U3104AccountingYearUpdateBody {
  return {
    id: data.id,
    yearCode: data.yearCode.trim(),
    version: data.version,
    accountingPeriods: data.accountingPeriods.map((p) => {
      const base: U3104AccountingPeriodUpdateBody = {
        periodNumber: p.periodNumber,
        startAt: toIsoBoundary(p.startAt),
        endAt: toIsoEndOfDayFromYmd(p.endAt),
      };
      if (p.id) {
        base.id = p.id;
        if (p.version !== undefined) {
          base.version = p.version;
        }
      }
      return base;
    }),
  };
}

export function buildDeleteBody(data: U3104AccountingYearFormRow): U3104AccountingYearDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const U3104_ERROR_YEAR_CODE_DUPLICATE = "BU310401";
export const U3104_ERROR_PERIOD_NOT_FOUND = "BU310402";
export const U3104_ERROR_ENTITY_ID_MISMATCH = "B00008";

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3104AccountingYearCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3104AccountingYearUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3104AccountingYearDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3104_ERROR_YEAR_CODE_DUPLICATE:
      errorResult.fieldErrors["yearCode"] = t("errYearCodeDuplicate", { ns: nameSpace });
      break;
    case U3104_ERROR_PERIOD_NOT_FOUND:
      errorResult.fieldErrors["accountingPeriods"] = t("errPeriodNotFound", { ns: nameSpace });
      break;
    case U3104_ERROR_ENTITY_ID_MISMATCH:
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errEntityIdMismatch", { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
